import { forwardRef } from 'react';
import { Menu } from '@mui/material';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { AppContextMenuItems } from './AppContextMenuItems';
import { useAppContextMenuBehavior } from './useAppContextMenuBehavior';
import type { AppContextMenuProps } from './AppContextMenu.types';

export const ModalAppContextMenu = forwardRef<HTMLDivElement, AppContextMenuProps>(function ModalAppContextMenu(
  {
    children,
    floatingPlacement: _floatingPlacement,
    header,
    items,
    mouseX,
    mouseY,
    noClose = false,
    onClose,
    width,
    maxHeight,
    offset,
    onKeyDown,
    slotProps,
    ...props
  },
  ref,
) {
  const { handleClose, handleCloseActions, handleKeyDown, mergedMenuRef } = useAppContextMenuBehavior({
    noClose,
    onClose,
    onKeyDown,
    ref,
  });

  return (
    <Menu
      ref={mergedMenuRef}
      {...props}
      onContextMenu={(e) => e.stopPropagation()}
      anchorReference={props.anchorEl ? 'anchorEl' : 'anchorPosition'}
      // Prevent viewport jumps when menus close due to MUI's built-in scroll-into-view behavior.
      disableRestoreFocus
      open={props.open}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      anchorPosition={props.open ? { top: mouseY || 0, left: mouseX || 0 } : undefined}
      slotProps={{
        paper: {
          sx: {
            [width ? 'width' : 'minWidth']: width || '240px',
            background: token.bg,
            border: `1px solid ${color.White04_T}`,
            borderRadius: 2,
            px: 0.5,
            maxHeight: maxHeight,
            ...(offset && {
              mt: offset.top,
              ml: offset.left,
            }),
          },
        },
        list: {
          sx: {
            py: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          },
        },
        ...slotProps,
      }}
    >
      <AppContextMenuItems header={header} items={items} onCloseActions={handleCloseActions}>
        {children}
      </AppContextMenuItems>
    </Menu>
  );
});
